import { MediaType } from 'src/app/sitos-x-api.types.g';

/**
 * Wartet darauf, dass eine Bedingung erfüllt ist (Wert nicht null/undefined)
 * @param checkFn - Funktion die den zu prüfenden Wert zurückgibt
 * @param pollInterval - Prüfintervall in ms (Standard: 100ms)
 * @param timeoutMs - Maximale Wartezeit in ms (Standard: 5000ms)
 * @returns Promise mit dem gefundenen Wert
 */
export function waitFor<T>(
  checkFn: () => T | null | undefined,
  pollInterval: number = 100,
  timeoutMs: number = 5000
): Promise<T> {
  
  return new Promise((resolve, reject) => {
    // Sofort prüfen - vielleicht existiert es bereits
    const immediateResult = checkFn();
    if (immediateResult != null) {
      resolve(immediateResult);
      return;
    }

    const startTime = Date.now();

    const intervalId = setInterval(() => {
      const result = checkFn();

      if (result != null) {
        clearInterval(intervalId);
        resolve(result);
        return;
      }

      if (Date.now() - startTime >= timeoutMs) {
        clearInterval(intervalId);
        reject(new Error(`Timeout: Bedingung nach ${timeoutMs}ms nicht erfüllt`));
      }
    }, pollInterval);
  });
}

/**
 * Berechnet einen neuen Hex-Farbwert durch Überblenden mit Schwarz oder Weiß.
 * Eräuterung:
 * Bei overlayColor #000000 und percentage 20 wird daraus ein Farbwert --variable-120,
 * bei overlayColor #ffffff und percentage 40 wird daraus ein Farbwert --variable-60
 * 
 * Basisformel: result = base × (1 - p) + overlay × p
 * 
 * @param baseColor    - Basisfarbe als Hex-String (z.B. "#4CAF50" oder "4CAF50")
 * @param overlayColor - Überlagerungsfarbe: "#000000" (Shade) oder "#FFFFFF" (Tint)
 * @param percentage   - Stärke des Overlays in % (0 = nur Base, 100 = nur Overlay)
 * @returns Neuer Hex-Farbwert als String (z.B. "#3a8c3e")
 */
export function blendHexColor(
  baseColor: string,
  overlayColor: string,
  percentage: number
): string {
  const clamp = (value: number) => Math.min(255, Math.max(0, Math.round(value)));

  const hexToRgb = (hex: string): [number, number, number] => {
    const clean = hex.replace('#', '');
    const full = clean.length === 3
      ? clean.split('').map(c => c + c).join('')
      : clean;
    return [
      parseInt(full.substring(0, 2), 16),
      parseInt(full.substring(2, 4), 16),
      parseInt(full.substring(4, 6), 16),
    ];
  };

  const rgbToHex = (r: number, g: number, b: number): string =>
    '#' + [r, g, b].map(v => v.toString(16).padStart(2, '0')).join('');

  const p = percentage / 100;
  const [br, bg, bb] = hexToRgb(baseColor);
  const [or, og, ob] = hexToRgb(overlayColor);

  const r = clamp(br * (1 - p) + or * p);
  const g = clamp(bg * (1 - p) + og * p);
  const b = clamp(bb * (1 - p) + ob * p);

  return rgbToHex(r, g, b);
}

/**
 * Konvertiert einen Hex-Farbwert (z.B. "#5761EE") in ein HSL-Array [h, s, l].
 * h: 0-360, s: 0-100, l: 0-100
 */
export function hexToHsl(hex: string): [number, number, number] {
  // Hex in RGB umwandeln
  let r = parseInt(hex.slice(1, 3), 16) / 255;
  let g = parseInt(hex.slice(3, 5), 16) / 255;
  let b = parseInt(hex.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }

  return [
    Math.round(h * 360),
    Math.round(s * 100),
    Math.round(l * 100)
  ];
}

/**
 * Berechnet die maximale Breite eines p-select basierend auf dem längsten Label
 * und setzt diese als feste Breite, damit sie nicht variiert.
 */
export function calcSelectWidth(labels: string[], font: string = '16px sans-serif'): string {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;
  ctx.font = font;

  const maxWidth = labels.reduce((max, label) => {
    return Math.max(max, ctx.measureText(label).width);
  }, 0);

  const padding = 60; // Innenabstand + Dropdown-Pfeil + Delete X
  return `${Math.ceil(maxWidth + padding)}px`;
}

 /**
   * Konvertiert einen Enum-Schlüsselnamen zu einem Translation-Key im SCREAMING_SNAKE_CASE Format.
   * 
   * Beispiele:
   * - "AlphabeticalAsc" → "ALPHABETICAL_ASC"
   * - "AlphabeticalDesc" → "ALPHABETICAL_DESC"
   * - "Importance" → "IMPORTANCE"
   * 
   * @param enumKey Der Schlüsselname des Enum (z.B. "AlphabeticalAsc")
   * @returns Der Translation-Key im SCREAMING_SNAKE_CASE Format
   */
  export function enumNameToTranslationKey(enumKey: string): string {
    return enumKey
      // Füge Unterstriche vor Großbuchstaben ein (außer am Anfang)
      .replace(/([a-z])([A-Z])/g, '$1_$2')
      // Konvertiere zu Großbuchstaben
      .toUpperCase();
  }

  import { ROLES } from '../../shared/contracts/roles';
import { ACCESS_RULES, AccessRule } from '../../shared/access/access-rules';
import { isSignal } from '@angular/core';

  /** localStorage-Schluessel, unter dem die Rollen-Claims des Tokens liegen. */
  const ROLE_CLAIMS_STORAGE_KEY = 'role_token_claims_obj';

  /**
   * Does the logged-in user have an administrative role? The roles are defined by
   * the rule ADMIN_AREA (shared/access/access-rules.ts).
   */
  export function isAdmin(): boolean {
    return canAccess('ADMIN_AREA');
  }

  /**
   * Does the logged-in user satisfy the named access rule from
   * shared/access/access-rules.ts (at least one of the rule's roles)?
   * The single check against the rules; UI only, the API secures the data.
   */
  export function canAccess(rule: AccessRule): boolean {
    return hasAnyRole(...ACCESS_RULES[rule]);
  }

  /**
   * Die Rollen des angemeldeten Benutzers aus den Token-Claims.
   *
   * EINZIGE Stelle, die die Claims liest – isAdmin(), hasRole() und hasAnyRole()
   * setzen alle hier auf.
   *
   * Defensiv: Fehlender, leerer oder kaputter Eintrag ergibt eine leere Liste –
   * niemals eine Exception.
   */
  export function getUserRoles(): readonly string[] {
    try {
      const raw = localStorage.getItem(ROLE_CLAIMS_STORAGE_KEY);
      if (!raw) return [];

      const parsed: unknown = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.filter((r): r is string => typeof r === 'string') : [];
    } catch {
      return [];
    }
  }

  /**
   * Besitzt der Benutzer GENAU diese Rolle?
   *
   * Gegenstueck zu isAdmin(), das mehrere Rollen zu "administrativ" zusammenfasst
   * (ADMIN, EDUCATION_MANAGER, UNIT_MANAGER). hasRole() trennt sie – gedacht fuer
   * Faelle, in denen eine feinere Unterscheidung noetig wird.
   *
   * @example hasRole(ROLES.ADMIN)
   */
  export function hasRole(role: ROLES): boolean {
    return getUserRoles().includes(role);
  }

  /** Besitzt der Benutzer mindestens eine der genannten Rollen? */
  export function hasAnyRole(...roles: readonly ROLES[]): boolean {
    const userRoles = getUserRoles();
    return roles.some(role => userRoles.includes(role));
  }

  export function generateUUID() {
    return crypto.randomUUID();
  }

  /** Protokolle, die openUrl() oeffnen darf. 'blob:' fuer selbst erzeugte
   *  Object-URLs (z. B. Kalender-Agenda-Export im Dashboard). */
  const ALLOWED_URL_PROTOCOLS: readonly string[] = ['http:', 'https:', 'blob:'];

  /**
   * true, wenn die URL (absolut oder relativ zur aktuellen Seite aufgeloest)
   * ein erlaubtes Protokoll hat. Blockt insbesondere javascript:- und data:-URLs.
   */
  function hasAllowedProtocol(url: string): boolean {
    try {
      return ALLOWED_URL_PROTOCOLS.includes(new URL(url, window.location.href).protocol);
    } catch {
      return false;
    }
  }

  /** Optionen fuer openUrl() – nur fuer target '_blank' relevant. */
  export interface OpenUrlOptions {
    /**
     * false laesst window.opener im geoeffneten Fenster stehen.
     *
     * NUR setzen, wenn die Zielseite den Rueckkanal braucht (z. B. der
     * SCORM-/grow-frame-Client, der sich per window.opener beim GrowTokenService
     * anmeldet). Sonst gilt: Ohne noopener kann die Zielseite ueber window.opener
     * das Ausgangsfenster manipulieren (Tabnabbing).
     * @default true
     */
    readonly noopener?: boolean;

    /**
     * false sendet den Referrer mit.
     *
     * Achtung: noreferrer IMPLIZIERT noopener. Solange noopener true ist, aendert
     * dieser Schalter am Opener-Verhalten nichts – er steuert dann nur, ob die
     * Zielseite die Herkunfts-URL erfaehrt.
     * @default true
     */
    readonly noreferrer?: boolean;
  }

  /**
   * Oeffnet eine URL – zentral, damit die Sicherheitsattribute nicht an jeder
   * Call-Site einzeln gesetzt werden muessen.
   *
   * Default ist das sichere Verhalten (noopener,noreferrer). Beides laesst sich
   * pro Aufruf abschalten; das ist bewusst umstaendlich zu schreiben, damit eine
   * Ausnahme im Code sichtbar bleibt und begruendet wird.
   *
   * Es werden nur http:-, https:- und blob:-URLs geoeffnet (relative Pfade
   * zaehlen als http/https der aktuellen Seite). Alles andere – insbesondere
   * javascript:- und data:-URLs – ist ein No-Op mit Konsolen-Warnung.
   *
   * @param url     Ziel-URL. Leer/undefined -> No-Op.
   * @param target  '_blank' (Default) oeffnet einen neuen Tab, '_self' navigiert
   *                im aktuellen Fenster.
   * @param options Siehe OpenUrlOptions. Bei '_self' wirkungslos.
   * @returns       Das Fenster-Handle, sofern eines entsteht. Mit noopener liefert
   *                der Browser immer null – dann gibt es bewusst keinen Zugriff.
   */
  export function openUrl(
    url: string | null | undefined,
    target: '_blank' | '_self' = '_blank',
    options: OpenUrlOptions = {},
  ): Window | null {
    if (!url) return null;

    if (!hasAllowedProtocol(url)) {
      console.warn('openUrl: blockiert, unerlaubtes URL-Protokoll:', url);
      return null;
    }

    if (target === '_self') {
      window.location.assign(url);
      return null;
    }

    const features: string[] = [];
    if (options.noopener ?? true) features.push('noopener');
    if (options.noreferrer ?? true) features.push('noreferrer');

    // Leerer features-String: window.open unterscheidet '' und undefined nicht
    // relevant – beides oeffnet einen normalen Tab ohne Einschraenkungen.
    return window.open(url, '_blank', features.join(','));
  }

  const EXCLUDE_MEDIA_TYPES = [
    MediaType.Task,
    MediaType.Event,
    MediaType.Link,
    MediaType.Chapter,
    MediaType.Lti,
  ] as const;
  
  type ExcludeMediaType = typeof EXCLUDE_MEDIA_TYPES[number];
  export type FilterableMediaType = Exclude<MediaType, ExcludeMediaType>;
  
  export type FilterableMediaKeys = {
    [K in keyof typeof MediaType]: (typeof MediaType)[K] extends FilterableMediaType ? K : never
  }[keyof typeof MediaType];
  
  export const FILTERABLE_MEDIA_TYPES_ARRAY = Object.values(MediaType).filter(
    (t): t is FilterableMediaType => !(EXCLUDE_MEDIA_TYPES as readonly string[]).includes(t)
  );
  
  export const FILTERABLE_MEDIA_TYPES = Object.fromEntries(
    Object.entries(MediaType).filter(
      ([_key, value]) => !(EXCLUDE_MEDIA_TYPES as readonly string[]).includes(value)
    )
  ) as Pick<typeof MediaType, FilterableMediaKeys>;


  export type ClassProperty = 'class' | 'styleClass';

  /**
   * Fügt eine CSS-Klasse hinzu.
   * @param prop Welches Feld genutzt wird ('class' oder 'styleClass'. Standard: 'styleClass')
   */
  export function addClass(item: any, className: string, prop: ClassProperty = 'styleClass') {
    if (!item[prop]) {
      item[prop] = className;
    } else if (!item[prop].split(' ').includes(className)) {
      item[prop] = `${item[prop]} ${className}`.trim();
    }
  }

  /**
   * Entfernt eine CSS-Klasse.
   */
  export function removeClass(item: any, className: string, prop: ClassProperty = 'styleClass') {
    if (item[prop]) {
      item[prop] = item[prop]
        .split(' ')
        .filter((c: string) => c !== className)
        .join(' ')
        .trim();
        
      // Optional: Wenn der String leer ist, das Feld auf undefined setzen, um das DOM sauber zu halten
      if (item[prop] === '') {
          item[prop] = undefined;
      }
    }
  }

  export function hasSignal(obj: unknown, name: string): boolean {
    return !!obj && typeof obj === 'object'
      && name in obj
      && isSignal((obj as Record<string, unknown>)[name]);
  }

  /**
   * Fokussierbare Elemente eines Dialogs, in Dokumentreihenfolge.
   *
   * `.p-hidden-focusable` ist ausgenommen: das sind die beiden unsichtbaren
   * Sentinel-Spans (tabindex="0"), die PrimeNGs Focus-Trap als erstes und letztes
   * Kind des Dialog-Containers einhaengt. Sie sind Umleitungspunkte, keine Ziele.
   */
  const DIALOG_FOCUSABLE_SELECTOR = [
    'button:not([disabled])',
    'a[href]',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ]
    .map(selector => `${selector}:not(.p-hidden-focusable)`)
    .join(', ');

  /**
   * Macht ein per `pTemplate="headless"` gebautes PrimeNG-Dialog-Panel zugaenglich:
   * setzt den Namen des Dialogs und holt den Fokus hinein.
   *
   * Beides leistet PrimeNG im headless-Template NICHT selbst:
   *  - `Dialog.focus()` greift auf contentViewChild/footerViewChild/headerViewChild
   *    zu. Diese ViewChilds existieren nur im `notHeadless`-Zweig des Dialog-
   *    Templates; mit headless sind alle drei `undefined`, `focusOnShow` laeuft also
   *    ins Leere und der Fokus bleibt auf dem auslosenden Element HINTER dem
   *    Overlay stehen (WCAG 2.4.3 / 2.1.2).
   *  - `aria-labelledby` zeigt auf eine generierte Header-Id, die es ohne
   *    PrimeNG-Header gar nicht gibt -- ein toter Verweis, der Dialog bleibt
   *    namenlos (4.1.2).
   *
   * Fokussiert wird das ERSTE fokussierbare Element im Dialog (in unseren Dialogen
   * der Schliesser) -- so auch der Regelfall der ARIA Authoring Practices. Den
   * Container selbst zu fokussieren waere zwar ebenfalls WCAG-konform, vertraegt
   * sich aber nicht mit PrimeNGs Focus-Trap: dessen erster Sentinel prueft
   * `!el.contains(relatedTarget)`, und weil `contains()` fuer das Element selbst
   * true ist, wertet er "kommt vom Container" wie "kommt von innen" und schickt den
   * Fokus an den LETZTEN Sentinel -- der erste Tab landete dadurch am Ende des
   * Dialogs. Der Container bleibt Traeger von Rolle und Name, nur nicht des Fokus.
   *
   * Der Griff ans DOM ist noetig, weil der Container ein PrimeNG-Element ist. Er
   * ist auf die selbst gesetzte styleClass begrenzt, nicht auf PrimeNG-Interna.
   * Aufzurufen im `(onShow)` des Dialogs -- dann existiert der Container.
   *
   * @param styleClass    styleClass des p-dialog (landet auf dem role="dialog"-Element)
   * @param labelledById  Id der Dialog-Ueberschrift im eigenen headless-Template
   */
  export function openDialogPanel(styleClass: string, labelledById: string): void {
    const panel = document.querySelector<HTMLElement>(`.${styleClass}`);
    if (!panel) return;

    panel.setAttribute('aria-labelledby', labelledById);

    const target = panel.querySelector<HTMLElement>(DIALOG_FOCUSABLE_SELECTOR);
    if (target) {
      target.focus();
      return;
    }

    // Notnagel fuer einen Dialog ohne jedes Steuerelement: dann doch der Container,
    // damit der Fokus ueberhaupt hineinkommt.
    panel.setAttribute('tabindex', '-1');
    panel.focus();
  }

  /**
   * Gibt den Fokus an das Element zurueck, das einen Dialog geoeffnet hat
   * (WCAG 2.4.3). PrimeNG merkt sich das auslosende Element nicht und stellt es
   * beim Schliessen auch nicht wieder her -- ohne das hier landet der Fokus nach
   * X / Escape / Backdrop auf `<body>`.
   *
   * Ein Element, das inzwischen aus dem DOM entfernt wurde (typisch, wenn das
   * Schliessen die Ansicht umbaut), ist nicht fokussierbar: `isConnected` faengt
   * das ab, damit der Aufrufer auf ein Ersatzziel ausweichen kann.
   *
   * @returns true, wenn der Fokus gesetzt wurde
   */
  export function restoreFocus(el: HTMLElement | null | undefined): boolean {
    if (!el?.isConnected) return false;

    el.focus();
    return true;
  }
